import React from "react";
import { clsx } from "clsx";
import {
    Byline,
    type BylineAuthor,
    BylineAuthorBio,
    type NotEmptyArray,
    Timestamp,
} from "@bloomberg/media-ui-article";
import type { PersonType } from "@bloomberg/media-ui-article/types/types";

import useLocalTimezone from "~hooks/useLocalTimezone/useLocalTimezone";
import { ARTICLE_TYPE, Person, Story } from "~types/types";
import areDatesEqual from "~utils/areDatesEqual";
import { getAuthorThumbnail } from "~utils/authorThumbnail";
import isOpinion from "~utils/isOpinion";

import styles from "./BasicByline.module.css";

interface OpinionBylineProps {
    className?: string;
    story: Story;
}

interface BylineTimestampProps {
    story: Story;
}

interface BasicBylineProps {
    className?: string;
    story: Story;
}

const ap_regex = /^ap$/i;
const pathPrefix = "/authors/";
const opinionPathPrefix = "/opinion/authors/";

const getFormattedByline = (story: Story) => {
    const { byline, type, attributor } = story;

    if (type === ARTICLE_TYPE.BREAKING_NEWS) {
        return byline || "Bloomberg News";
    }

    if (attributor && ap_regex.test(attributor)) {
        return "Associated Press";
    }

    return byline || "";
};

const getBylineAuthors = (
    authors: Person[],
    isOpinionArticle: boolean
): PersonType[] =>
    authors?.map((author) => {
        const { profileUrl = "", thumbnail } = author;
        const profileUrlWithPrefix = isOpinionArticle
            ? `${opinionPathPrefix}${profileUrl}`
            : `${pathPrefix}${profileUrl}`;

        const thumbnailUrl = getAuthorThumbnail(author);

        return {
            ...author,
            profileUrl: profileUrlWithPrefix,
            thumbnail: {
                ...thumbnail,
                url: thumbnailUrl,
            },
        } as PersonType;
    }) || [];

const BylineTimestamp = ({
    story,
}: BylineTimestampProps): JSX.Element | null => {
    const { corrected, publishedAt, updatedAt } = story;
    const timezoneInfo = useLocalTimezone();
    // show updatedAt when publishedAt and updatedAt are not same
    const showUpdatedAt = !areDatesEqual(publishedAt, updatedAt);

    return (
        <div className={styles.timestamp}>
            <Timestamp dateString={publishedAt} {...timezoneInfo} />

            {showUpdatedAt && (
                <span className={styles.updatedTimestamp}>
                    {corrected ? "Corrected " : "Updated on "}{" "}
                    <Timestamp
                        className={styles.updatedTimestamp}
                        dateString={updatedAt}
                        {...timezoneInfo}
                    />
                </span>
            )}
        </div>
    );
};

const OpinionByline = ({
    className,
    story,
}: OpinionBylineProps): JSX.Element | null => {
    const { authors = [] } = story;
    const bylineAuthors = getBylineAuthors(authors, true);

    return (
        <div className={clsx(styles.byline, styles.opinionArticle, className)}>
            <BylineTimestamp story={story} />

            {bylineAuthors.length && (
                <BylineAuthorBio authors={bylineAuthors} />
            )}
        </div>
    );
};

const BasicByline = ({
    story,
    className,
}: BasicBylineProps): JSX.Element | null => {
    const { authors = [], language = "en" } = story;
    const isOpinionArticle = isOpinion(story);

    // Opinion Article bylines are rendered with author bio
    if (isOpinionArticle) {
        return <OpinionByline className={className} story={story} />;
    }

    // basic byline format for thp and breaking news articles
    const formattedByline = getFormattedByline(story);

    // Formatted byline author info, returns an empty array when authors are not available
    const formattedAuthors = getBylineAuthors(authors, false);

    const bylineAuthors =
        authors?.length > 0
            ? getBylineAuthors(authors, false)
            : [{ name: formattedByline }];

    return (
        <div className={clsx(styles.byline, className)}>
            {Boolean(formattedAuthors.length || formattedByline) && (
                <Byline
                    authors={bylineAuthors as NotEmptyArray<BylineAuthor>}
                    className={styles.bylineAuthor}
                    language={language}
                />
            )}

            <BylineTimestamp story={story} />
        </div>
    );
};

export default BasicByline;
